import { faQuestion } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useApi } from '@hooks/useApi';
import useModalManager from '@hooks/useModalManager';
import usePusherSubscribe from '@hooks/usePusherSubscribe';
import { Blockquote, Button, Skeleton } from '@ui';
import { cx } from 'class-variance-authority';
import { CompletedAnimation } from 'components/Animations/CompletedAnimation';
import QRCode from 'components/QRCode';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';

type User = { [x: string]: any };

interface Props {
    onAuthentication?: (token: string, user: User) => void;
    onRefresh?: () => void;
    className?: string;
    disableRefresh?: boolean;
}

const AppLogin = ({ onAuthentication, onRefresh, className, disableRefresh }: Props) => {
    const api = useApi();
    const expirationTimer = useRef<NodeJS.Timer>(undefined);
    const intervalTimer = useRef<NodeJS.Timer>(undefined);
    const [timeLeft, setTimeLeft] = useState(0);
    const [expiresAt, setExpiresAt] = useState<Date>();
    const [qrCodeHash, setQrCodeHash] = useState<string>();
    const [channel, setChannel] = useState<string>();
    const [isLoading, setIsLoading] = useState(true);
    const [success, setSuccess] = useState(false);
    const [user, setUser] = useState<User | null>(null);
    const { addModal } = useModalManager();
    const createLoginHashRef = useRef<() => void>(() => {});
    const expiredWhileHidden = useRef(false);

    // a forgotten background tab would otherwise create a new hash row every few minutes
    const refreshWhenVisible = useCallback(() => {
        if (document.hidden) {
            expiredWhileHidden.current = true;
            return;
        }
        createLoginHashRef.current();
    }, []);

    const createLoginHash = useCallback(() => {
        setIsLoading(true);
        onRefresh?.();
        setUser(null);
        setSuccess(false);
        clearInterval(expirationTimer.current);
        clearInterval(intervalTimer.current);

        void api({
            url: 'login/hash/generate',
            method: 'POST',
            onSuccess: (data) => {
                setIsLoading(false);
                setQrCodeHash(data.hash);
                setChannel(data.channel);
                setExpiresAt(new Date(data.expires_at));
                expirationTimer.current = setInterval(
                    refreshWhenVisible,
                    moment(data.expires_at).diff(moment(), 'milliseconds')
                );
            },
            onError: () => {
                setIsLoading(false);
                setQrCodeHash(undefined);
                setChannel(undefined);
                setExpiresAt(undefined);
            }
        });
    }, [onRefresh, refreshWhenVisible]);

    useEffect(() => {
        createLoginHashRef.current = createLoginHash;
    }, [createLoginHash]);

    const validateHash = useCallback(() => {
        if (!qrCodeHash || success) return;

        void api({
            url: `login/hash`,
            method: 'POST',
            data: { hash: qrCodeHash },
            onSuccess: (data) => {
                if (data?.token) {
                    onAuthentication?.(data.token, data.user);
                    setUser(data.user);
                    setSuccess(true);
                    clearInterval(expirationTimer.current);
                    clearInterval(intervalTimer.current);
                }
            }
        });
    }, [qrCodeHash, success]);

    const openHelpPopup = useCallback(() => {
        addModal({
            title: 'QR-Code-Login in der Sportision App',
            content: (
                <div className="tw-space-y-4">
                    <Blockquote>
                        Mit dem QR-Code-Login meldest du dich schnell und sicher in Sportision an – entweder direkt über
                        deine Kamera oder über den integrierten Scanner in der App.
                    </Blockquote>
                    <Blockquote title="1. Direkt über die Kamera">
                        <ol className="tw-mb-0">
                            <li>Öffne die Kamera-App deines Smartphones.</li>
                            <li>Richte sie auf den angezeigten QR-Code.</li>
                            <li>Tippe auf die erscheinende Benachrichtigung, um den Login zu starten.</li>
                        </ol>
                    </Blockquote>
                    <Blockquote title="2. Über den integrierten Scanner in der Sportision App">
                        <ol className="tw-mb-0">
                            <li>Öffne die Sportision App.</li>
                            <li>Gehe in den Bereich „Benutzer“.</li>
                            <li>Tippe oben auf das QR-Code-Symbol.</li>
                            <li>Richte die Kamera auf den angezeigten QR-Code, um den Login durchzuführen.</li>
                        </ol>
                    </Blockquote>
                </div>
            )
        });
    }, []);

    useEffect(() => {
        if (success || !expiresAt) return;
        setTimeLeft(Math.abs(moment().diff(expiresAt, 'seconds')));
        intervalTimer.current = setInterval(() => {
            setTimeLeft(Math.abs(moment().diff(expiresAt, 'seconds')));
        }, 1000);
        // the confirm arrives via Pusher; this slow poll only catches a missed event
        const pollTimer = setInterval(() => {
            if (!document.hidden) validateHash();
        }, 15000);

        return () => {
            clearInterval(intervalTimer.current);
            clearInterval(pollTimer);
        };
    }, [expiresAt, validateHash, success]);

    usePusherSubscribe(success ? null : channel, (event) => {
        if (event === 'qr-login.confirmed') validateHash();
    });

    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.hidden || !expiredWhileHidden.current) return;
            expiredWhileHidden.current = false;
            createLoginHashRef.current();
        };
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, []);

    useEffect(() => {
        createLoginHash();

        return () => {
            clearInterval(expirationTimer.current);
        };
    }, []);

    return (
        <div className={cx('tw-rounded-2xl tw-border tw-border-solid tw-border-accents-2 tw-p-4', className)}>
            <div className="tw-relative tw-mb-2 tw-flex tw-aspect-square tw-w-full tw-items-center tw-justify-center">
                {success ? (
                    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2">
                        <CompletedAnimation />
                        <span className="tw-block tw-text-center tw-text-lg tw-text-accents-8">
                            Hallo {user?.name}!
                        </span>
                    </div>
                ) : (
                    (isLoading || qrCodeHash) && (
                        <>
                            <Skeleton rounded={false} className="tw-absolute tw-inset-0" />
                            {!isLoading && (
                                <QRCode
                                    containerClassName="tw-absolute tw-inset-0"
                                    value={`sprtsn://login/${encodeURIComponent(qrCodeHash)}`}
                                />
                            )}
                        </>
                    )
                )}
            </div>
            {!success && (
                <div className="tw-my-4">
                    <span className="tw-mb-2 tw-block tw-text-center tw-text-accents-8">
                        Jetzt einloggen per QR Code! Einfach mit dem Handy scannen und in der Sportision App freigeben.
                    </span>
                    {isLoading ? (
                        <Skeleton className="tw-mx-auto tw-h-4 tw-w-40" />
                    ) : (
                        <span className="tw-block tw-animate-pulse tw-text-center tw-text-xs tw-italic tw-text-accents-6">
                            Neuer QR Code in {timeLeft} Sekunden
                        </span>
                    )}
                </div>
            )}
            <div className="tw-flex tw-items-center tw-gap-4">
                <Button
                    block
                    onPress={() => {
                        setSuccess(false);
                        createLoginHash();
                    }}
                    disabled={isLoading || disableRefresh}
                >
                    {success ? 'App Login neustarten' : 'Neuen QR Code generieren'}
                </Button>
                <Button onPress={openHelpPopup} color="secondary">
                    <FontAwesomeIcon icon={faQuestion} />
                    Hilfe
                </Button>
            </div>
        </div>
    );
};

export default AppLogin;
